import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Check } from "lucide-react";
import { toPng } from "html-to-image";
import type { Win } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Curated palette of backgrounds. Each entry knows whether its text should
 * be light or dark — the caller doesn't need to think about contrast.
 */
type Theme = {
  id: string;
  name: string;
  bg: string;       // CSS background value (solid or gradient)
  text: "light" | "dark";
  accent: string;   // secondary text / line color
};

const THEMES: Theme[] = [
  { id: "terracotta", name: "Terracotta", bg: "#C0592C", text: "light", accent: "rgba(255,255,255,0.7)" },
  { id: "navy",       name: "Navy",       bg: "#0E2230", text: "light", accent: "rgba(255,255,255,0.65)" },
  { id: "forest",     name: "Forest",     bg: "#1E4631", text: "light", accent: "rgba(255,255,255,0.65)" },
  { id: "plum",       name: "Plum",       bg: "#3B2A5A", text: "light", accent: "rgba(255,255,255,0.7)" },
  { id: "cream",      name: "Cream",      bg: "#F5EDE0", text: "dark",  accent: "rgba(30,25,20,0.55)" },
  { id: "blush",      name: "Blush",      bg: "#F4D0D9", text: "dark",  accent: "rgba(40,20,35,0.55)" },
  { id: "coral-grad", name: "Sunset",     bg: "linear-gradient(135deg, #EC6F5F 0%, #8D5AE0 100%)", text: "light", accent: "rgba(255,255,255,0.75)" },
  { id: "teal-grad",  name: "Tide",       bg: "linear-gradient(135deg, #0E2230 0%, #1F6B7A 100%)",  text: "light", accent: "rgba(255,255,255,0.65)" },
  { id: "warm-grad",  name: "Ember",      bg: "linear-gradient(135deg, #B8341E 0%, #F0B247 100%)",  text: "light", accent: "rgba(255,255,255,0.75)" },
];

type Ratio = "landscape" | "square";
const RATIOS: { id: Ratio; label: string; w: number; h: number }[] = [
  { id: "landscape", label: "Landscape", w: 1200, h: 630 },
  { id: "square",    label: "Square",    w: 1080, h: 1080 },
];

export function WinShareImageModal({ win, onClose }: { win: Win; onClose: () => void }) {
  const [theme, setTheme] = useState<Theme>(THEMES[0]);
  const [ratio, setRatio] = useState<Ratio>("landscape");
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const dims = RATIOS.find((r) => r.id === ratio)!;
  const bodyText = win.title || win.quote || "Felix Win";
  const secondary = win.title && win.quote ? win.quote : null;
  const attribution = [win.account, win.source, win.date].filter(Boolean).join(" · ");

  // Light-text themes get white text; dark-text themes get deep navy
  const fg = theme.text === "light" ? "#FFFFFF" : "#1A1A2E";

  // Scale body text to ratio. Square gets tighter line-height for balance.
  const titleSize = ratio === "landscape" ? 56 : 64;
  const secondarySize = ratio === "landscape" ? 22 : 26;

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        width: dims.w,
        height: dims.h,
        cacheBust: true,
        skipFonts: false,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      const safeTitle = (win.title ?? "felix-win").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${safeTitle}-${ratio}.png`;
      a.click();
    } catch (err) {
      console.error("image export failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const copyImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        width: dims.w,
        height: dims.h,
        cacheBust: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } else {
        // Fallback: just download
        download();
      }
    } catch {
      download();
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-lg"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-card/95 px-5 py-4">
          <div className="text-h4 font-semibold text-foreground">Share as image</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-4 overflow-y-auto p-4 md:gap-6 md:p-6 md:grid-cols-[minmax(0,1fr)_220px]">
          {/* Preview */}
          <div className="flex min-w-0 items-center justify-center rounded-xl bg-muted/30 p-3 md:p-4">
            {(() => {
              // Modal (720) − padding (48) − gap (24) − controls col (220) −
              // preview inner padding (32) leaves ~396px. Cap at 380 to be safe.
              const maxPreviewW = ratio === "landscape" ? 380 : 320;
              const scale = maxPreviewW / dims.w;
              const previewW = dims.w * scale;
              const previewH = dims.h * scale;
              return (
                <div
                  style={{
                    width: previewW,
                    height: previewH,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 12,
                    boxShadow: "0 12px 30px -12px rgba(0,0,0,0.25)",
                  }}
                >
                  <div
                    ref={cardRef}
                    style={{
                      width: dims.w,
                      height: dims.h,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                      background: theme.bg,
                      color: fg,
                      padding: ratio === "landscape" ? "84px 96px" : "110px 110px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      fontFamily: "'Fraunces', 'Iowan Old Style', Georgia, serif",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                {/* Big decorative quote */}
                <div
                  aria-hidden
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 180,
                    fontWeight: 600,
                    lineHeight: 0.8,
                    color: fg,
                    opacity: 0.25,
                    position: "absolute",
                    top: ratio === "landscape" ? 40 : 60,
                    left: ratio === "landscape" ? 72 : 90,
                    userSelect: "none",
                  }}
                >
                  &ldquo;
                </div>

                <div style={{ marginTop: ratio === "landscape" ? 80 : 110, position: "relative", zIndex: 1 }}>
                  <div
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: titleSize,
                      fontWeight: 500,
                      lineHeight: 1.15,
                      letterSpacing: "-0.015em",
                      color: fg,
                      maxWidth: ratio === "landscape" ? "900px" : "860px",
                    }}
                  >
                    {bodyText}
                  </div>
                  {secondary && (
                    <div
                      style={{
                        marginTop: 28,
                        fontFamily: "'Fraunces', serif",
                        fontSize: secondarySize,
                        lineHeight: 1.5,
                        color: theme.accent,
                        maxWidth: ratio === "landscape" ? "820px" : "820px",
                      }}
                    >
                      {secondary.length > 260 ? secondary.slice(0, 260).trim() + "…" : secondary}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: 24,
                  }}
                >
                  <div style={{ color: theme.accent, fontSize: 20, fontFamily: "Inter, sans-serif", letterSpacing: "0.02em" }}>
                    {attribution || "Felix Launch"}
                  </div>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: fg,
                      opacity: 0.8,
                    }}
                  >
                    Felix Wins
                  </div>
                </div>
              </div>
            </div>
              );
            })()}
          </div>

          {/* Controls */}
          <div className="space-y-5">
            <div>
              <div className="mb-2 text-caption font-semibold text-muted-foreground">Ratio</div>
              <div className="flex gap-1 rounded-md bg-muted/40 p-1">
                {RATIOS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setRatio(r.id)}
                    className={cn(
                      "flex-1 rounded px-2 py-1.5 text-caption font-medium transition-colors",
                      ratio === r.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 text-caption font-semibold text-muted-foreground">Background</div>
              <div className="grid grid-cols-3 gap-2">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t)}
                    title={t.name}
                    className={cn(
                      "group relative h-14 overflow-hidden rounded-lg border-2 transition-all",
                      theme.id === t.id ? "border-primary scale-105" : "border-transparent hover:border-border"
                    )}
                    style={{ background: t.bg }}
                  >
                    <span
                      className="absolute inset-x-0 bottom-0 truncate bg-black/30 px-1.5 py-0.5 text-[10px] font-medium text-white"
                    >
                      {t.name}
                    </span>
                    {theme.id === t.id && (
                      <span className="absolute right-1 top-1 rounded-full bg-white p-0.5 text-primary">
                        <Check size={10} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <button
                onClick={download}
                disabled={downloading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-60"
              >
                <Download size={14} />
                {downloading ? "Rendering…" : "Download PNG"}
              </button>
              <button
                onClick={copyImage}
                disabled={downloading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-caption font-medium text-foreground hover:bg-muted disabled:opacity-60"
              >
                {copied ? <><Check size={14} className="text-emerald-600" /> Copied image</> : "Copy image to clipboard"}
              </button>
              <p className="text-micro leading-snug text-muted-foreground">
                Image renders at {dims.w}×{dims.h}px (retina). Perfect for Slack, LinkedIn, or Twitter.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
